// =======================================================
// ParticleStorm — dense particle field with proximity links
// =======================================================
//
// Two objects:
//   1) A THREE.Points mesh with per-vertex position + velocity + alpha.
//      The vertex shader handles integration (pos += vel*dt) and wrapping.
//   2) A THREE.LineSegments mesh for proximity links, built CPU-side
//      using a spatial hash. Lines are cheap if we keep the link-range
//      sensible.
//
// At 10900×540 with 1500 particles and a link-range of ~150, this hits
// 60fps easily on a mid-range GPU.

import * as THREE from 'three';
import { Generator } from './Generator.js';

const VERT = /* glsl */ `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }
`;

const FRAG = /* glsl */ `
  varying vec2 vUv;
  uniform sampler2D uMap;
  uniform vec3 uBg;
  void main() {
    vec4 t = texture2D(uMap, vUv);
    vec3 col = mix(uBg, t.rgb / max(t.a, 0.001), t.a);
    gl_FragColor = vec4(col, 1.0);
  }
`;

export class ParticleStorm extends Generator {
  constructor() {
    super({
      name: 'Particle Storm',
      category: 'intense',
      schema: {
        count:      { default: 1400, min: 100,  max: 4000, step: 10,   label: 'Count' },
        speed:      { default: 60,   min: 0,    max: 500,  step: 1,    label: 'Speed' },
        size:       { default: 4,    min: 1,    max: 24,   step: 0.5,  label: 'Size' },
        glow:       { default: 0.9,  min: 0,    max: 2,    step: 0.01, label: 'Glow' },
        link:       { default: 180,  min: 0,    max: 500,  step: 1,    label: 'Link range (px)' },
        linkAlpha:  { default: 0.45, min: 0,    max: 1,    step: 0.01, label: 'Link alpha' },
        flow:       { default: 0.4,  min: 0,    max: 2,    step: 0.01, label: 'Flow noise' },
        turbulence: { default: 0.2,  min: 0,    max: 1.5,  step: 0.01, label: 'Turbulence' },
        accent:     { default: '#62ffd8', type: 'color',               label: 'Color' },
      }
    });
    this._maxCount = 4000;        // allocate max upfront to avoid re-buffering
    this._linkCap  = 3000;        // max simultaneous link segments rendered
    this.version = 'canvas-fullscreen-v2';
  }

  onEnter(scene, engine) {
    this.engine = engine;

    this.off = document.createElement('canvas');
    this.offCtx = this.off.getContext('2d', { alpha: true });
    this._resizeCanvas(engine.width, engine.height);

    this.tex = new THREE.CanvasTexture(this.off);
    this.tex.minFilter = THREE.LinearFilter;
    this.tex.magFilter = THREE.LinearFilter;
    this.tex.generateMipmaps = false;

    const geo = new THREE.PlaneGeometry(2, 2);
    const mat = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: {
        uMap: { value: this.tex },
        uBg:  { value: new THREE.Color('#000000') },
      },
      transparent: false,
      depthWrite: false,
      depthTest: false
    });
    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.frustumCulled = false;
    this._fitMeshToCamera(engine.width, engine.height);
    scene.add(this.mesh);
    this.track(this.mesh);
    this.track(this.tex);

    // internal particle state (CPU-side for collision + integration)
    this.particles = [];
    this._seedParticles();
  }

  _seedParticles() {
    this.particles = [];
    for (let i = 0; i < this._maxCount; i++) {
      this.particles.push({
        x0: this.rand01(300 + i * 9),
        y0: this.rand01(301 + i * 9),
        ampX: this.randRange(302 + i * 9, 0.015, 0.12),
        ampY: this.randRange(303 + i * 9, 0.04, 0.28),
        freqX: Math.max(1, Math.floor(this.randRange(304 + i * 9, 1, 8))),
        freqY: Math.max(1, Math.floor(this.randRange(305 + i * 9, 1, 8))),
        phase: this.rand01(306 + i * 9),
        a: this.randRange(307 + i * 9, 0.5, 1.0)
      });
    }
  }

  reseed() {
    this._seedParticles();
  }

  onResize(w, h) {
    this._resizeCanvas(w, h);
    this._fitMeshToCamera(w, h);
  }

  _fitMeshToCamera(w, h) {
    if (!this.mesh) return;
    this.mesh.scale.set(w / Math.max(1, h), 1, 1);
  }

  onParamChange(key, val) {
    // Canvas renderer reads params on every frame.
  }

  _resizeCanvas(w, h) {
    const maxW = 4096;
    const maxH = 1080;
    this.off.width = Math.max(2, Math.min(maxW, Math.round(w)));
    this.off.height = Math.max(2, Math.min(maxH, Math.round(h)));
    this._scale = this.off.width / Math.max(1, w);
  }

  _rgba(hex, alpha) {
    const h = String(hex || '#ffffff').replace('#', '');
    const r = h.length >= 6 ? parseInt(h.substring(0, 2), 16) : parseInt((h[0] || 'f') + (h[0] || 'f'), 16);
    const g = h.length >= 6 ? parseInt(h.substring(2, 4), 16) : parseInt((h[1] || 'f') + (h[1] || 'f'), 16);
    const b = h.length >= 6 ? parseInt(h.substring(4, 6), 16) : parseInt((h[2] || 'f') + (h[2] || 'f'), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  }

  update(dt, time, engine) {
    const v = this.values;
    const ctx = this.offCtx;
    const w = this.off.width;
    const h = this.off.height;
    const count = Math.min(Math.floor(v.count), this._maxCount);
    const loop = engine.getLoopInfo(time);

    ctx.clearRect(0, 0, w, h);
    ctx.globalCompositeOperation = 'lighter';

    // --- integrate particles ---
    for (let i = 0; i < count; i++) {
      const p = this.particles[i];
      const base = (loop.phase * (v.speed / 40) + p.phase) * Math.PI * 2;
      const flowX = Math.sin(base * p.freqX) * p.ampX * w;
      const flowY = Math.cos(base * p.freqY) * p.ampY * h;
      const swirlX = Math.sin(base * (p.freqY + 1)) * v.flow * 0.06 * w;
      const swirlY = Math.cos(base * (p.freqX + 1)) * v.flow * 0.08 * h;
      const jitterX = Math.sin(base * (p.freqX + p.freqY) + p.phase * 9.0) * v.turbulence * 0.025 * w;
      const jitterY = Math.cos(base * (p.freqX + p.freqY) + p.phase * 9.0) * v.turbulence * 0.035 * h;
      p.x = Math.max(0, Math.min(w, p.x0 * w + flowX + swirlX + jitterX));
      p.y = Math.max(0, Math.min(h, p.y0 * h + flowY + swirlY + jitterY));
    }

    // --- spatial hash for links ---
    if (v.link > 0 && v.linkAlpha > 0) {
      const linkPx = v.link * this._scale;
      const linkSq = linkPx * linkPx;
      const cellSize = Math.max(4, linkPx);
      const cols = Math.max(1, Math.ceil(w / cellSize));
      const rows = Math.max(1, Math.ceil(h / cellSize));
      const grid = new Array(cols * rows);
      for (let i = 0; i < grid.length; i++) grid[i] = [];
      for (let i = 0; i < count; i++) {
        const p = this.particles[i];
        const c = Math.min(cols - 1, Math.max(0, Math.floor(p.x / cellSize)));
        const r = Math.min(rows - 1, Math.max(0, Math.floor(p.y / cellSize)));
        grid[r * cols + c].push(i);
      }
      let linkCount = 0;
      ctx.lineWidth = Math.max(1, this._scale);
      outer:
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const cell = grid[r * cols + c];
          // check self + 4 neighbors (right, down-left, down, down-right)
          for (let dr = 0; dr <= 1; dr++) {
            for (let dc = -1; dc <= 1; dc++) {
              if (dr === 0 && dc < 0) continue;
              const nr = r + dr, nc = c + dc;
              if (nr < 0 || nr >= rows || nc < 0 || nc >= cols) continue;
              const other = grid[nr * cols + nc];
              for (let ii = 0; ii < cell.length; ii++) {
                const i = cell[ii];
                const startJ = (dr === 0 && dc === 0) ? ii + 1 : 0;
                for (let jj = startJ; jj < other.length; jj++) {
                  const j = other[jj];
                  if (i === j) continue;
                  const a = this.particles[i], b = this.particles[j];
                  const dx = a.x - b.x, dy = a.y - b.y;
                  const d2 = dx * dx + dy * dy;
                  if (d2 < linkSq) {
                    const al = (1 - d2 / linkSq) * v.linkAlpha * 0.32;
                    ctx.strokeStyle = this._rgba(v.accent, al);
                    ctx.beginPath();
                    ctx.moveTo(a.x, a.y);
                    ctx.lineTo(b.x, b.y);
                    ctx.stroke();
                    linkCount++;
                    if (linkCount >= this._linkCap) break outer;
                  }
                }
              }
            }
          }
        }
      }
    }

    const dotSize = Math.max(0.7, v.size * this._scale);
    for (let i = 0; i < count; i++) {
      const p = this.particles[i];
      const r = dotSize * (0.55 + p.a * 0.7);
      const glowR = r * (1.6 + v.glow * 1.8);
      const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, glowR);
      grad.addColorStop(0, this._rgba(v.accent, p.a));
      grad.addColorStop(0.35, this._rgba(v.accent, p.a * 0.45));
      grad.addColorStop(1, this._rgba(v.accent, 0));
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(p.x, p.y, glowR, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.globalCompositeOperation = 'source-over';
    this.tex.needsUpdate = true;
  }
}
